import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { getVesselFinderIdentifiers } from "../utils/vesselFinderUtils";

const MapWrapper = styled.div<{ $marginBottom: number }>`
  width: 100%;
  margin-bottom: ${(props) => props.$marginBottom}px;
`;

const MapContainer = styled.div<{ $height: number | string }>`
  width: 100%;
  height: ${(props) =>
    typeof props.$height === "number" ? `${props.$height}px` : props.$height};
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const VesselFinderLink = styled.a`
  display: inline-block;
  margin-top: 8px;
`;

interface VesselFinderMapProps {
  imo?: string;
  mmsi?: string;
  showTrack?: boolean;
  height?: number | string;
  marginBottom?: number;
}

export const VesselFinderMap: React.FC<VesselFinderMapProps> = ({
  imo: rawImo,
  mmsi,
  showTrack = true,
  height = 400,
  marginBottom = 24,
}) => {
  const { t } = useTranslation();
  const identifiers = getVesselFinderIdentifiers(rawImo ?? "");
  const explicitMmsi = getVesselFinderIdentifiers(mmsi ?? "").mmsi;
  const imo = identifiers.imo;
  const resolvedMmsi = explicitMmsi ?? identifiers.mmsi;
  const vesselId = imo ?? resolvedMmsi;
  const requestedHeight =
    typeof height === "number" ? height : parseInt(height, 10);
  const embedHeight = Number.isFinite(requestedHeight)
    ? Math.max(requestedHeight, 400)
    : 400;

  const getVesselFinderUrl = () => {
    const baseUrl = "https://www.vesselfinder.com/aismap";
    const params = new URLSearchParams({
      zoom: "3",
      lat: "undefined",
      lon: "undefined",
      width: "100%",
      height: String(embedHeight),
      names: "true",
      track: showTrack.toString(),
      fleet: "false",
      fleet_name: "false",
      fleet_hide_old_positions: "false",
      clicktoact: "false",
      store_pos: "true",
      ra: typeof window === "undefined" ? "" : window.location.href,
      ...(imo ? { imo } : {}),
      ...(resolvedMmsi ? { mmsi: resolvedMmsi } : {}),
    });
    return `${baseUrl}?${params.toString()}`;
  };

  if (!vesselId) {
    return null;
  }

  return (
    <MapWrapper $marginBottom={marginBottom}>
      <MapContainer $height={height}>
        <StyledIframe
          src={getVesselFinderUrl()}
          title="VesselFinder Map"
          allowFullScreen
        />
      </MapContainer>
      <VesselFinderLink
        href={`https://www.vesselfinder.com/vessels/details/${vesselId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("bookings.openInVesselFinder")}
      </VesselFinderLink>
    </MapWrapper>
  );
};
